import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface DriftedAssignment {
  storeId: number;
  storeName: string;
  mediaFormatId: number;
  mediaFormatName: string;
  plannedOperatorId: number;
  plannedOperatorName: string;
  currentOperatorId: number | null;
  currentOperatorName: string | null;
  source: 'system' | 'manual';
  // ADDED is intentionally not emitted because the diff is snapshot-keyed.
  diffType: 'CHANGED' | 'REMOVED' | 'ADDED';
}

interface DriftDetectionResult {
  inSync: boolean;
  totalAssignments: number;
  driftCount: number;
  driftedAssignments: DriftedAssignment[];
}

interface OperatorAssignmentDriftModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driftData: DriftDetectionResult | undefined;
  isLoading: boolean;
  error: Error | null;
  campaignId: number;
  isDraft: boolean;
  onReconcile: (selectedKeys: Array<{ storeId: number; mediaFormatId: number }>) => Promise<void>;
  isReconciling: boolean;
}

export function OperatorAssignmentDriftModal({
  open,
  onOpenChange,
  driftData,
  isLoading,
  error,
  campaignId,
  isDraft,
  onReconcile,
  isReconciling
}: OperatorAssignmentDriftModalProps) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Determine which rows are selectable
  const selectableRows = useMemo(() => {
    if (!driftData) return new Set<string>();
    
    const selectable = new Set<string>();
    for (const drift of driftData.driftedAssignments) {
      // Only selectable if:
      // - source is 'system' (not manual)
      // - diffType is 'CHANGED' (not REMOVED)
      // - currentOperatorId is present
      if (
        drift.source === 'system' && 
        drift.diffType === 'CHANGED' && 
        drift.currentOperatorId !== null
      ) {
        const key = `${drift.storeId}|${drift.mediaFormatId}`;
        selectable.add(key);
      }
    }
    return selectable;
  }, [driftData]);

  // Preselect all selectable rows when drift data changes
  React.useEffect(() => {
    if (driftData && open && selectableRows.size > 0) {
      setSelectedKeys(new Set(selectableRows));
    }
  }, [driftData, open, selectableRows]);

  const handleToggleRow = (storeId: number, mediaFormatId: number) => {
    const key = `${storeId}|${mediaFormatId}`;
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedKeys.size === selectableRows.size) {
      // Deselect all
      setSelectedKeys(new Set());
    } else {
      // Select all
      setSelectedKeys(new Set(selectableRows));
    }
  };

  const handleApply = async () => {
    const keys = Array.from(selectedKeys).map(key => {
      const [storeId, mediaFormatId] = key.split('|').map(Number);
      return { storeId, mediaFormatId };
    });

    await onReconcile(keys);
    setShowConfirmDialog(false);
    setSelectedKeys(new Set());
  };

  const isRowSelectable = (drift: DriftedAssignment) => {
    const key = `${drift.storeId}|${drift.mediaFormatId}`;
    return selectableRows.has(key);
  };

  const getRowDisabledReason = (drift: DriftedAssignment) => {
    if (drift.source === 'manual') {
      return 'Manual assignments are preserved and cannot be automatically updated';
    }
    if (drift.diffType === 'REMOVED') {
      return 'Cannot update to a removed assignment (no current operator)';
    }
    if (drift.currentOperatorId === null) {
      return 'No current operator assigned';
    }
    return null;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Operator Assignment Check</DialogTitle>
          <DialogDescription>
            Checks whether store-format operator assignments have changed since this campaign was created.
            {isDraft ? ' Draft campaigns can update selected rows.' : ' Read-only.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-4 py-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Error checking assignments</span>
            </div>
            <p className="mt-2">{error.message}</p>
          </div>
        )}

        {!isLoading && !error && driftData && (
          <div className="space-y-4">
            {/* In-sync state */}
            {driftData.inSync && (
              <div className="rounded-md bg-green-50 p-4 border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">All store-format operator assignments are in sync.</span>
                </div>
                <p className="text-sm text-green-700 mt-2">
                  All {driftData.totalAssignments} assignments match the current operator configuration.
                </p>
              </div>
            )}

            {/* Drift detected state */}
            {!driftData.inSync && (
              <>
                <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">
                      {driftData.driftCount} of {driftData.totalAssignments} store-format operator assignments have changed
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 mt-2">
                    These assignments differ from when the campaign was created. Review the changes below.
                  </p>
                </div>

                {/* Action buttons for draft campaigns */}
                {isDraft && selectableRows.size > 0 && (
                  <div className="flex items-center justify-between gap-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="text-sm text-blue-800">
                      <strong>{selectedKeys.size}</strong> of <strong>{selectableRows.size}</strong> selectable assignments selected
                    </div>
                    <Button
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={selectedKeys.size === 0 || isReconciling}
                      variant="default"
                      size="sm"
                    >
                      {isReconciling ? 'Updating...' : `Update ${selectedKeys.size} selected`}
                    </Button>
                  </div>
                )}

                {/* Drifted assignments table */}
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isDraft && selectableRows.size > 0 && (
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedKeys.size === selectableRows.size && selectableRows.size > 0}
                              onCheckedChange={handleToggleAll}
                              aria-label="Select all"
                            />
                          </TableHead>
                        )}
                        <TableHead>Store</TableHead>
                        <TableHead>Format</TableHead>
                        <TableHead>Planned Operator</TableHead>
                        <TableHead>Current Operator</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driftData.driftedAssignments.map((drift, idx) => {
                        const key = `${drift.storeId}|${drift.mediaFormatId}`;
                        const selectable = isRowSelectable(drift);
                        const disabledReason = getRowDisabledReason(drift);
                        
                        return (
                          <TableRow key={idx}>
                            {isDraft && selectableRows.size > 0 && (
                              <TableCell>
                                {selectable ? (
                                  <Checkbox
                                    checked={selectedKeys.has(key)}
                                    onCheckedChange={() => handleToggleRow(drift.storeId, drift.mediaFormatId)}
                                    aria-label={`Select ${drift.storeName} - ${drift.mediaFormatName}`}
                                  />
                                ) : (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div>
                                          <Checkbox disabled checked={false} />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-xs">{disabledReason}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="font-medium">{drift.storeName}</TableCell>
                            <TableCell>{drift.mediaFormatName}</TableCell>
                            <TableCell>
                              <span className="text-muted-foreground">
                                {drift.plannedOperatorName}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={drift.diffType === 'REMOVED' ? 'text-destructive' : ''}>
                                {drift.currentOperatorName || '(Removed)'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={drift.source === 'manual' ? 'default' : 'secondary'} className="text-xs">
                                {drift.source === 'manual' ? 'Manual' : 'System'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Manual assignments notice */}
                {driftData.driftedAssignments.some(d => d.source === 'manual') && (
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                    <strong>Note:</strong> Manual assignments are preserved and will not be automatically updated.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Operator Assignments?</AlertDialogTitle>
            <AlertDialogDescription>
              This will update the campaign snapshot's planned operator for <strong>{selectedKeys.size}</strong> store-format rows.
              <br /><br />
              <strong>What will change:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Operator assignments will be updated to match current configuration</li>
              </ul>
              <br />
              <strong>What will NOT change:</strong>
              <ul className="list-disc list-inside space-y-1">
                <li>Stores, formats, quantities remain unchanged</li>
                <li>Manual overrides will not be changed</li>
                <li>Campaign structure and tasks remain intact</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReconciling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApply} disabled={isReconciling}>
              {isReconciling ? 'Updating...' : 'Update Selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
